import React, { createContext, useContext, useCallback, useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { empresa as empresaFallback } from "../mock/mock";
import { permDe } from "../utils/permissoes";

const AppContext = createContext(null);

const AUTH_KEY = "crsj_auth_v1";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const setAuthHeader = (token) => {
  if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete axios.defaults.headers.common["Authorization"];
};

export const formatBRL = (v) =>
  (v === null || v === undefined) ? "—" : (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const formatDate = (d) => {
  if (!d) return "-";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
};

export const calcularIdade = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y) return null;
  const hoje = new Date();
  let idade = hoje.getFullYear() - y;
  const mesAtual = hoje.getMonth() + 1;
  if (mesAtual < m || (mesAtual === m && hoje.getDate() < d)) idade -= 1;
  return idade;
};

const emptyData = {
  produtos: [], residentes: [], estoqueResidente: {},
  fornecimentos: [], consumos: [], faturasPagas: {}, faturasAjustes: {}, faturasCanceladas: {}, faturasPagamentos: {}, fornecedores: [], categoriasConta: [], contasPagar: [], contasReceber: [], inventario: [], alertas: {}, categorias: [], empresa: null,
  servicosExtras: [], residenteServicos: [], fechamentoDiasAntes: 5,
};

export function AppProvider({ children }) {
  const [data, setData] = useState(emptyData);
  const [loading, setLoading] = useState(true);
  const [auth, setAuth] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(AUTH_KEY));
      // Descarta sessões antigas (anteriores ao JWT) que não possuem token
      if (stored && !stored.token) {
        localStorage.removeItem(AUTH_KEY);
        return null;
      }
      return stored || null;
    } catch {
      return null;
    }
  });

  // Mantém o header Authorization sincronizado com a sessão (antes de qualquer chamada)
  if (typeof axios.defaults.headers.common["Authorization"] === "undefined" && auth?.token) {
    setAuthHeader(auth.token);
  }

  useEffect(() => {
    localStorage.setItem(AUTH_KEY, JSON.stringify(auth));
    setAuthHeader(auth?.token);
  }, [auth]);

  const refreshSeq = useRef(0);
  const [boletos, setBoletos] = useState({});
  const [historicoBoletos, setHistoricoBoletos] = useState({});
  const carregarBoletos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/faturas/boletos`);
      setBoletos(res.data || {});
      const h = await axios.get(`${API}/faturas/boletos/historico`);
      setHistoricoBoletos(h.data || {});
    } catch (e) { /* não-admin ou sem acesso: ignora */ }
  }, []);
  const refresh = useCallback(async () => {
    const seq = ++refreshSeq.current;
    try {
      const res = await axios.get(`${API}/bootstrap`);
      // Só aplica a resposta se ainda for a mais recente (evita que um bootstrap
      // antigo em voo sobrescreva os dados recém-atualizados após um POST/PUT).
      if (seq === refreshSeq.current) setData(res.data);
    } catch (e) {
      console.error("Erro ao carregar dados", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auth?.token) { refresh(); carregarBoletos(); }
    else setLoading(false);
  }, [refresh, carregarBoletos, auth?.token]);

  // Se qualquer chamada retornar 401 (token expirado/ausente), encerra a sessão
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const url = String(err?.config?.url || "");
        if (err?.response?.status === 401 && !url.includes("/auth/login")) {
          setAuthHeader(null);
          localStorage.removeItem(AUTH_KEY);
          setAuth(null);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  // ---------- AUTH ----------
  const login = async (email, senha) => {
    try {
      const res = await axios.post(`${API}/auth/login`, { email, senha });
      const { token, usuario } = res.data;
      setAuthHeader(token);
      setAuth({ ...usuario, token });
      return { ok: true };
    } catch (e) {
      const status = e?.response?.status;
      if (!status || status >= 500) {
        return { ok: false, error: "Servidor ou banco de dados indisponível no momento. Não é um problema de senha — tente novamente em alguns minutos." };
      }
      return { ok: false, error: e?.response?.data?.detail || "E-mail ou senha inválidos." };
    }
  };
  const logout = () => { setAuthHeader(null); setAuth(null); };

  // ---------- CHAT / AVISOS (estado compartilhado: sino, auto-abrir, card de aviso) ----------
  const CHAT_ROLES = ["administrador", "enfermeiro", "cuidador", "tecnico_enfermagem", "medico", "nutricionista", "fisioterapeuta"];
  const chatHabilitado = !!auth?.token && (auth?.perfil === "familia" || CHAT_ROLES.includes(auth?.perfil));
  const [chatUI, setChatUI] = useState({ open: false, tab: "conversas" });
  const openChat = useCallback((tab = "conversas") => setChatUI({ open: true, tab }), []);
  const closeChat = useCallback(() => setChatUI((s) => ({ ...s, open: false })), []);
  const [naoLidasChat, setNaoLidasChat] = useState({ total: 0, mensagens: 0, avisos: 0 });
  const chatOpenRef = useRef(false);
  useEffect(() => { chatOpenRef.current = chatUI.open; }, [chatUI.open]);
  const carregarNaoLidasChat = useCallback(async () => {
    if (!chatHabilitado) return;
    try {
      const r = await axios.get(`${API}/chat/nao-lidas`);
      const nd = r.data || { total: 0, mensagens: 0, avisos: 0 };
      setNaoLidasChat(nd);
      const total = nd.total || 0;
      // Abre o chat automaticamente UMA vez por item novo (inclui logar com não lidos),
      // sem reabrir a cada refresh nem depois que o usuário fechar (persistido por usuário).
      const uid = auth?.id || auth?.email || "u";
      const key = `chat_autoopen_${uid}`;
      let visto = parseInt(localStorage.getItem(key) || "0", 10);
      if (isNaN(visto)) visto = 0;
      if (total > visto && total > 0 && !chatOpenRef.current) {
        setChatUI({ open: true, tab: (nd.avisos || 0) > 0 ? "avisos" : "conversas" });
      }
      if (total !== visto) localStorage.setItem(key, String(total));
    } catch (e) { /* ignore */ }
  }, [chatHabilitado, auth]);
  useEffect(() => {
    if (!chatHabilitado) return;
    carregarNaoLidasChat();
    const t = setInterval(carregarNaoLidasChat, 12000);
    return () => clearInterval(t);
  }, [chatHabilitado, carregarNaoLidasChat]);

  // ---------- DERIVED ----------
  const estoqueEmpresa = useMemo(
    () => Object.fromEntries(data.produtos.map((p) => [p.id, p.estoqueEmpresa || 0])),
    [data.produtos]
  );

  const getProduto = (id) => data.produtos.find((p) => p.id === id);
  const getResidente = (id) => data.residentes.find((r) => r.id === id);

  // Janela de consumo (ciclo de cobrança) da fatura de um residente em um mês de cobrança.
  const janelaConsumo = (residenteId, mes) => {
    const r = data.residentes.find((x) => x.id === residenteId);
    let dias = 5;
    if (r && r.fechamentoDiasAntes !== null && Number.isFinite(r.fechamentoDiasAntes)) dias = r.fechamentoDiasAntes;
    else if (Number.isFinite(data.fechamentoDiasAntes)) dias = data.fechamentoDiasAntes;
    const dia = Math.min(Number(r?.diaPagamento) || 10, 28);
    const inter = { mensal: 1, trimestral: 3, semestral: 6, anual: 12 };
    const n = inter[r?.periodicidadePagamento || "mensal"] || 1;
    const [y, m] = (mes || "").split("-").map(Number);
    if (!y) return null;
    const cur = new Date(y, m - 1, dia); cur.setDate(cur.getDate() - dias);
    // fechamento do mês de cobrança anterior (N meses antes)
    const pa = (y * 12 + m) - n; const py = Math.floor((pa - 1) / 12); const pm = ((pa - 1) % 12) + 1;
    const prev = new Date(py, pm - 1, dia); prev.setDate(prev.getDate() - dias);
    const inicio = new Date(prev); inicio.setDate(inicio.getDate() + 1);
    return { inicio, fim: cur };
  };
  const estoqueDoResidente = (rid, pid) => (data.estoqueResidente[rid]?.[pid] || 0);
  const estoqueDaEmpresa = (pid) => (estoqueEmpresa[pid] || 0);

  // ---------- ACTIONS ----------
  const registrarFornecimento = async (payload, opts = {}) => {
    const res = await axios.post(`${API}/fornecimentos`, payload);
    if (res.data?.id) setData((d) => ({ ...d, fornecimentos: [res.data, ...(d.fornecimentos || [])] }));
    if (!opts.skipRefresh) refresh();
    return res.data;
  };
  const editarFornecimento = async (id, payload) => {
    const res = await axios.put(`${API}/fornecimentos/${id}`, payload);
    if (res.data?.id) setData((d) => ({ ...d, fornecimentos: (d.fornecimentos || []).map((x) => (x.id === id ? res.data : x)) }));
    refresh();
    return res.data;
  };
  const excluirFornecimento = async (id) => {
    setData((d) => ({ ...d, fornecimentos: (d.fornecimentos || []).filter((x) => x.id !== id) }));
    await axios.delete(`${API}/fornecimentos/${id}`);
    refresh();
  };

  const registrarConsumo = async (payload, opts = {}) => {
    const res = await axios.post(`${API}/consumos`, payload);
    if (res.data?.id) setData((d) => ({ ...d, consumos: [res.data, ...(d.consumos || [])] }));
    if (!opts.skipRefresh) refresh();
    return res.data; // {qtdResidente, qtdEmpresa, valorCobrado, ...}
  };
  const editarConsumo = async (id, payload) => {
    const res = await axios.put(`${API}/consumos/${id}`, payload);
    if (res.data?.id) setData((d) => ({ ...d, consumos: (d.consumos || []).map((x) => (x.id === id ? res.data : x)) }));
    refresh();
    return res.data;
  };
  const excluirConsumo = async (id) => {
    setData((d) => ({ ...d, consumos: (d.consumos || []).filter((x) => x.id !== id) }));
    await axios.delete(`${API}/consumos/${id}`);
    refresh();
  };

  const salvarProduto = async (prod) => {
    const r = prod.id ? await axios.put(`${API}/produtos/${prod.id}`, prod) : await axios.post(`${API}/produtos`, prod);
    refresh();
    return r.data;
  };

  const entradaEstoqueEmpresa = async (produtoId, quantidade) => {
    await axios.post(`${API}/produtos/${produtoId}/entrada`, { quantidade: Number(quantidade) });
    refresh();
  };

  const excluirProduto = async (produtoId) => {
    setData((d) => ({ ...d, produtos: (d.produtos || []).filter((p) => p.id !== produtoId) }));
    await axios.delete(`${API}/produtos/${produtoId}`);
    refresh();
  };

  const salvarPrecosMassa = async (itens) => {
    await axios.post(`${API}/produtos/precos-massa`, { itens });
    refresh();
  };

  const salvarFaturaAjuste = async (residenteId, mes, itens, observacao) => {
    await axios.post(`${API}/faturas/ajuste`, { residenteId, mes, itens, observacao });
    refresh();
  };
  const excluirFaturaAjuste = async (residenteId, mes) => {
    await axios.delete(`${API}/faturas/ajuste/${residenteId}/${mes}`);
    refresh();
  };
  const excluirFatura = async (key) => {
    const idx = key.lastIndexOf("_");
    const residenteId = key.slice(0, idx);
    const mes = key.slice(idx + 1);
    await axios.delete(`${API}/faturas/${residenteId}/${mes}`);
    refresh();
  };
  const restaurarFatura = async (key) => {
    const idx = key.lastIndexOf("_");
    const residenteId = key.slice(0, idx);
    const mes = key.slice(idx + 1);
    await axios.post(`${API}/faturas/restaurar`, { residenteId, mes, paga: false });
    refresh();
  };
  const enviarFaturaWhatsapp = async (residenteId, mes, total) =>
    (await axios.post(`${API}/faturas/enviar-whatsapp`, { residenteId, mes, total })).data;
  const enviarFaturaWhatsappPdf = async (residenteId, mes, total, pdfBase64) =>
    (await axios.post(`${API}/faturas/enviar-whatsapp-pdf`, { residenteId, mes, total, pdfBase64 })).data;
  const gerarBoletoFatura = async (residenteId, mes, opts = {}) => {
    const r = (await axios.post(`${API}/faturas/gerar-boleto`, { residenteId, mes, ...opts })).data;
    // atualização otimista do mapa de boletos + refetch em segundo plano
    setBoletos((m) => ({
      ...m,
      [`${residenteId}_${mes}`]: {
        status: r.status || "PENDING", valor: r.valor, vencimento: r.vencimento,
        bankSlipUrl: r.bankSlipUrl, invoiceUrl: r.invoiceUrl, asaasPaymentId: r.asaasPaymentId,
        criadoEm: new Date().toISOString(),
        sandbox: /sandbox/i.test(`${r.bankSlipUrl || ""}${r.invoiceUrl || ""}`),
      },
    }));
    carregarBoletos();
    return r;
  };
  const cancelarBoletoFatura = async (residenteId, mes) => {
    const r = (await axios.post(`${API}/faturas/cancelar-boleto`, { residenteId, mes })).data;
    setBoletos((m) => { const n = { ...m }; delete n[`${residenteId}_${mes}`]; return n; });
    carregarBoletos();
    return r;
  };
  const obterPixFatura = async (residenteId, mes) => (await axios.get(`${API}/faturas/${residenteId}/${mes}/pix`)).data;
  const baixarFaturaPdf = async (residenteId, mes, nomeArquivo) => {
    const res = await axios.get(`${API}/faturas/${residenteId}/${mes}/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = nomeArquivo || `fatura-${mes}.pdf`; a.click();
    setTimeout(() => window.URL.revokeObjectURL(url), 30000);
  };
  const enviarPixFatura = async (residenteId, mes) => (await axios.post(`${API}/faturas/${residenteId}/${mes}/pix/enviar`)).data;
  const baixaPixFatura = async (payload) => {
    const r = (await axios.post(`${API}/faturas/baixa-pix`, payload)).data;
    carregarBoletos();
    refresh();
    return r;
  };

  const baixarContrato = async (residenteId) => {
    const r = await axios.get(`${API}/residentes/${residenteId}/contrato`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    window.open(url, "_blank");
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  };
  const enviarContratoEmail = async (residenteId, email) =>
    (await axios.post(`${API}/residentes/${residenteId}/contrato/enviar-email`, { email })).data;

  const baixarDocumento = async (residenteId, key) => {
    const r = await axios.get(`${API}/residentes/${residenteId}/documento/${key}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    window.open(url, "_blank");
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  };
  const enviarDocumentoEmail = async (residenteId, key, email) =>
    (await axios.post(`${API}/residentes/${residenteId}/documento/${key}/enviar-email`, { email })).data;
  const baixarRelatorio = async (tipo) => {
    const r = await axios.get(`${API}/relatorios/${tipo}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    window.open(url, "_blank");
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  };

  const salvarEmpresa = async (dados) => {
    const r = await axios.put(`${API}/empresa`, dados);
    setData((d) => ({ ...d, empresa: r.data }));
    refresh();
    return r.data;
  };

  const salvarResidente = async (res) => {
    let r;
    if (res.id) r = await axios.put(`${API}/residentes/${res.id}`, res);
    else r = await axios.post(`${API}/residentes`, res);
    const upd = r.data;
    if (upd && upd.id) {
      setData((d) => {
        const existe = (d.residentes || []).some((x) => x.id === upd.id);
        const residentes = existe
          ? d.residentes.map((x) => (x.id === upd.id ? upd : x))
          : [...(d.residentes || []), upd];
        return { ...d, residentes };
      });
    }
    refresh();
    return upd;
  };

  const inativarResidente = async (rid, motivo, opts = {}) => {
    const { data: upd } = await axios.post(`${API}/residentes/${rid}/inativar`, {
      motivo, data: opts.data, gerarFatura: !!opts.gerarFatura,
    });
    setData((d) => ({ ...d, residentes: (d.residentes || []).map((r) => (r.id === rid ? upd : r)) }));
    refresh();
    return upd;
  };
  const reativarResidente = async (rid) => {
    const { data: upd } = await axios.post(`${API}/residentes/${rid}/reativar`);
    setData((d) => ({ ...d, residentes: (d.residentes || []).map((r) => (r.id === rid ? upd : r)) }));
    refresh();
  };

  const salvarCategoria = async (cat) => {
    if (cat.id) await axios.put(`${API}/categorias/${cat.id}`, { nome: cat.nome });
    else await axios.post(`${API}/categorias`, { nome: cat.nome });
    refresh();
  };
  const excluirCategoria = async (id) => {
    await axios.delete(`${API}/categorias/${id}`);
    refresh();
  };

  const marcarFaturaPaga = async (key, paga) => {
    const idx = key.lastIndexOf("_");
    const residenteId = key.slice(0, idx);
    const mes = key.slice(idx + 1);
    await axios.post(`${API}/faturas/pagar`, { residenteId, mes, paga });
    refresh();
  };
  const keyToRM = (key) => { const idx = key.lastIndexOf("_"); return { residenteId: key.slice(0, idx), mes: key.slice(idx + 1) }; };
  const baixaMassaFaturas = async (keys) => {
    await axios.post(`${API}/faturas/baixa-massa`, { faturas: keys.map(keyToRM) });
    refresh();
  };
  const excluirMassaFaturas = async (keys) => {
    await axios.post(`${API}/faturas/excluir-massa`, { faturas: keys.map(keyToRM) });
    refresh();
  };

  // ---------- FORNECEDORES ----------
  const salvarFornecedor = async (f) => {
    const r = f.id ? await axios.put(`${API}/fornecedores/${f.id}`, f) : await axios.post(`${API}/fornecedores`, f);
    refresh();
    return r.data;
  };
  const excluirFornecedor = async (id) => { await axios.delete(`${API}/fornecedores/${id}`); refresh(); };

  // ---------- CATEGORIAS DE CONTA ----------
  const salvarCategoriaConta = async (nome) => { const r = await axios.post(`${API}/categorias-conta`, { nome }); refresh(); return r.data; };
  const updateCategoriaConta = async (id, nome) => { const r = await axios.put(`${API}/categorias-conta/${id}`, { nome }); refresh(); return r.data; };
  const excluirCategoriaConta = async (id) => { await axios.delete(`${API}/categorias-conta/${id}`); refresh(); };

  // ---------- CONTAS A PAGAR ----------
  const salvarConta = async (c) => {
    const r = c.id ? await axios.put(`${API}/contas-pagar/${c.id}`, c) : await axios.post(`${API}/contas-pagar`, c);
    refresh();
    return r.data;
  };
  const salvarOrcamentos = async (orcamentos) => { const r = await axios.put(`${API}/orcamentos-categoria`, { orcamentos }); refresh(); return r.data; };
  const excluirConta = async (id, escopo = "este") => { await axios.delete(`${API}/contas-pagar/${id}`, { params: { escopo } }); refresh(); };
  const gerarRecorrentes = async () => { await axios.post(`${API}/contas-pagar/gerar-recorrentes`); refresh(); };
  const testarAvisoContas = async () => (await axios.post(`${API}/contas-pagar/enviar-aviso`)).data;
  const baixaMassaContas = async (ids) => { await axios.post(`${API}/contas-pagar/baixa-massa`, { ids }); refresh(); };
  const excluirMassaContas = async (ids) => { await axios.post(`${API}/contas-pagar/excluir-massa`, { ids }); refresh(); };

  // ---------- CONTAS A RECEBER (recebíveis manuais) ----------
  const salvarContaReceber = async (c) => {
    const r = c.id ? await axios.put(`${API}/contas-receber/${c.id}`, c) : await axios.post(`${API}/contas-receber`, c);
    const saved = r.data;
    setData((d) => {
      const list = d.contasReceber || [];
      if (saved && saved.parcelado && Array.isArray(saved.contas)) return { ...d, contasReceber: [...list, ...saved.contas] };
      if (c.id && saved && saved.id) return { ...d, contasReceber: list.map((x) => (x.id === c.id ? saved : x)) };
      if (saved && saved.id) return { ...d, contasReceber: [...list, saved] };
      return d;
    });
    refresh();
    return saved;
  };
  const excluirContaReceber = async (id) => {
    await axios.delete(`${API}/contas-receber/${id}`);
    setData((d) => ({ ...d, contasReceber: (d.contasReceber || []).filter((x) => x.id !== id) }));
    refresh();
  };

  // ---------- INVENTÁRIO ----------
  const salvarInventario = async (i) => {
    if (i.id) await axios.put(`${API}/inventario/${i.id}`, i);
    else await axios.post(`${API}/inventario`, i);
    refresh();
  };
  const excluirInventario = async (id) => { await axios.delete(`${API}/inventario/${id}`); refresh(); };

  // ---------- DOCUMENTOS ANEXADOS ----------
  const patchResidente = (upd) => setData((d) => ({ ...d, residentes: (d.residentes || []).map((r) => (r.id === upd.id ? upd : r)) }));
  const uploadDocumento = async (rid, file, categoria, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data: res } = await axios.post(`${API}/residentes/${rid}/documentos/upload?categoria=${encodeURIComponent(categoria)}`, fd, {
      headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
      onUploadProgress: (ev) => onProgress && ev.total && onProgress(Math.round((ev.loaded * 100) / ev.total)),
    });
    if (res.residente) patchResidente(res.residente);
    return res.doc;
  };
  const getDocumento = async (rid, docId) =>
    (await axios.get(`${API}/residentes/${rid}/documentos/${docId}`)).data;
  const getDocumentoBlob = async (rid, docId) =>
    (await axios.get(`${API}/residentes/${rid}/documentos/${docId}/arquivo`, { responseType: "blob", timeout: 180000 })).data;
  const getDocumentoThumb = async (rid, docId) =>
    (await axios.get(`${API}/residentes/${rid}/documentos/${docId}/miniatura`, { responseType: "blob", timeout: 30000 })).data;
  const deleteDocumento = async (rid, docId) => {
    const { data: res } = await axios.delete(`${API}/residentes/${rid}/documentos/${docId}`);
    if (res.residente) patchResidente(res.residente); else refresh();
  };
  const patchDocumento = async (rid, docId, body) => {
    const { data: res } = await axios.patch(`${API}/residentes/${rid}/documentos/${docId}`, body);
    if (res.residente) patchResidente(res.residente);
    return res.doc;
  };
  const getDocumentoFamiliaBlob = async (rid, docId, miniatura = false) =>
    (await axios.get(`${API}/familia/residentes/${rid}/documentos/${docId}/${miniatura ? "miniatura" : "arquivo"}`, { responseType: "blob", timeout: 180000 })).data;

  // ---------- PRONTUÁRIO (PEP) ----------
  const getProntuario = async (rid) =>
    (await axios.get(`${API}/residentes/${rid}/prontuario`)).data;
  const addRegistroProntuario = async (rid, payload) =>
    (await axios.post(`${API}/residentes/${rid}/prontuario/registros`, payload)).data;
  const updateProntuarioInfo = async (rid, info) =>
    (await axios.put(`${API}/residentes/${rid}/prontuario/info`, info)).data;
  const gerarAnotacaoInteligente = async (rid) =>
    (await axios.post(`${API}/residentes/${rid}/anotacao-inteligente`)).data;

  // ---------- RECEITUÁRIO / PROFISSIONAIS ----------
  const getReceituarios = async (rid) => (await axios.get(`${API}/residentes/${rid}/receituarios`)).data;
  const createReceituario = async (rid, payload) =>
    (await axios.post(`${API}/residentes/${rid}/receituarios`, payload)).data;
  const deleteReceituario = async (rid, regId) => { await axios.delete(`${API}/residentes/${rid}/receituarios/${regId}`); };
  const getProfissionais = async () => (await axios.get(`${API}/profissionais`)).data;
  const createProfissional = async (p) => (await axios.post(`${API}/profissionais`, p)).data;
  const updateProfissional = async (pid, p) => (await axios.put(`${API}/profissionais/${pid}`, p)).data;
  const deleteProfissional = async (pid) => { await axios.delete(`${API}/profissionais/${pid}`); };
  const getReceitaModelos = async () => (await axios.get(`${API}/receita-modelos`)).data;
  const createReceitaModelo = async (m) => (await axios.post(`${API}/receita-modelos`, m)).data;
  const deleteReceitaModelo = async (mid) => { await axios.delete(`${API}/receita-modelos/${mid}`); };
  const getMedicacaoModelos = async () => (await axios.get(`${API}/medicacao-modelos`)).data;
  const createMedicacaoModelo = async (m) => (await axios.post(`${API}/medicacao-modelos`, m)).data;
  const deleteMedicacaoModelo = async (mid) => { await axios.delete(`${API}/medicacao-modelos/${mid}`); };
  const deleteRegistroProntuario = async (rid, regId) => {
    await axios.delete(`${API}/residentes/${rid}/prontuario/registros/${regId}`);
  };
  const updateRegistroProntuario = async (rid, regId, payload) =>
    (await axios.put(`${API}/residentes/${rid}/prontuario/registros/${regId}`, payload)).data;

  // ---------- NOTIFICAÇÕES / WHATSAPP ----------
  const getNotificacoes = async (params = {}) => (await axios.get(`${API}/notificacoes`, { params })).data;
  const limparNotificacoes = async () => (await axios.post(`${API}/notificacoes/limpar`)).data;
  const enviarNotificacao = async (nid) => (await axios.post(`${API}/notificacoes/${nid}/enviar`)).data;
  const runFechamento = async () => (await axios.post(`${API}/rotinas/fechamento`)).data;
  const getWhatsappStatus = async () => (await axios.get(`${API}/whatsapp/status`)).data;

  // ---------- USUÁRIOS / PERFIS ----------
  const getUsuarios = async () => (await axios.get(`${API}/usuarios`)).data;
  const createUsuario = async (u) => (await axios.post(`${API}/usuarios`, u)).data;
  const updateUsuario = async (uid, u) => (await axios.put(`${API}/usuarios/${uid}`, u)).data;
  const deleteUsuario = async (uid) => { await axios.delete(`${API}/usuarios/${uid}`); };
  const trocarSenha = async (senhaAtual, senhaNova) =>
    (await axios.post(`${API}/auth/trocar-senha`, { senhaAtual, senhaNova })).data;

  const trocarFoto = async (foto) => {
    const r = await axios.post(`${API}/auth/foto`, { foto });
    setAuth((a) => (a ? { ...a, foto } : a));
    refresh();
    return r.data;
  };

  // RBAC granular por módulo. Admin sempre pode. Sem permissões custom → comportamento legado.
  const _permsCustom = () => {
    const p = auth?.permissoes;
    return p && typeof p === "object" && Object.keys(p).length > 0 ? p : null;
  };
  const podeVer = (modulo) => {
    if (auth?.perfil === "administrador") return true;
    const p = _permsCustom();
    if (p) return !!permDe(p, modulo)?.ver;
    return null; // legado: decidir por papel no menu
  };
  const podeAcao = (modulo, acao) => {
    if (auth?.perfil === "administrador") return true;
    const p = _permsCustom();
    if (p) return !!permDe(p, modulo)?.[acao];
    return true; // legado: mantém botões como antes
  };

  // ---------- SERVIÇOS EXTRAS (catálogo) + SERVIÇOS DO RESIDENTE ----------
  const salvarServicoExtra = async (s) => {
    if (s.id) {
      const r = await axios.put(`${API}/servicos-extras/${s.id}`, s);
      setData((d) => ({ ...d, servicosExtras: (d.servicosExtras || []).map((x) => (x.id === s.id ? r.data : x)) }));
    } else {
      const r = await axios.post(`${API}/servicos-extras`, s);
      setData((d) => ({ ...d, servicosExtras: [...(d.servicosExtras || []), r.data] }));
    }
    refresh();
  };
  const excluirServicoExtra = async (id) => {
    await axios.delete(`${API}/servicos-extras/${id}`);
    setData((d) => ({ ...d, servicosExtras: (d.servicosExtras || []).filter((x) => x.id !== id) }));
    refresh();
  };
  const salvarResidenteServico = async (rid, s) => {
    if (s.id) {
      const r = await axios.put(`${API}/residentes/${rid}/servicos/${s.id}`, s);
      setData((d) => ({ ...d, residenteServicos: (d.residenteServicos || []).map((x) => (x.id === s.id ? r.data : x)) }));
    } else {
      const r = await axios.post(`${API}/residentes/${rid}/servicos`, s);
      setData((d) => ({ ...d, residenteServicos: [...(d.residenteServicos || []), r.data] }));
    }
    refresh();
  };
  const excluirResidenteServico = async (rid, sid) => {
    await axios.delete(`${API}/residentes/${rid}/servicos/${sid}`);
    setData((d) => ({ ...d, residenteServicos: (d.residenteServicos || []).filter((x) => x.id !== sid) }));
    refresh();
  };

  const perfil = auth?.perfil || null;
  const isAdmin = perfil === "administrador";
  const acessoFinanceiro = !!(isAdmin || auth?.acessoFinanceiro);
  const verValores = !!(isAdmin || auth?.verValores);
  const podeReceituario = perfil === "administrador" || perfil === "enfermeiro";
  const podeApagarAnotacao = perfil === "administrador" || perfil === "enfermeiro";
  const podeEditarAnotacaoSempre = ["administrador", "enfermeiro", "medico", "nutricionista", "fisioterapeuta"].includes(perfil);

  // ---------- FATURAS (derivadas) ----------
  const faturas = useMemo(() => {
    const map = {};
    const FATURAMENTO_INICIO = data.inicioFaturamento || "2026-09"; // configurável em Integrações; nenhuma fatura antes deste mês
    const resById = Object.fromEntries(data.residentes.map((r) => [r.id, r]));
    const ensure = (residenteId, mes) => {
      // Descarta qualquer mês anterior ao início do faturamento (evita faturas AUTOMÁTICAS retroativas).
      if (!mes || mes < FATURAMENTO_INICIO) return { itens: [], extras: [], manual: [], total: 0 };
      const key = `${residenteId}_${mes}`;
      if (!map[key]) map[key] = { key, residenteId, mes, itens: [], extras: [], manual: [], observacao: "", total: 0 };
      return map[key];
    };
    // Faturas manuais (avulsas) criadas pelo admin NÃO respeitam o piso — sempre registram.
    const ensureManual = (residenteId, mes) => {
      if (!mes) return { itens: [], extras: [], manual: [], total: 0 };
      const key = `${residenteId}_${mes}`;
      if (!map[key]) map[key] = { key, residenteId, mes, itens: [], extras: [], manual: [], observacao: "", total: 0 };
      return map[key];
    };
    // Mensalidade vigente em um mês. O valor informado (valorBase) é o valor ATUAL
    // (já reajustado). O reajuste de 8% é relativo ao ciclo vigente: 0 no ciclo atual,
    // +1 a cada aniversário futuro do contrato e -1 para cada ciclo passado.
    const mensalidadeVigenteMes = (dataInicio, valorBase, ano, mes) => {
      const base = Number(valorBase) || 0;
      if (!base) return 0;
      const [sy, sm] = (dataInicio || "").split("-").map(Number);
      if (!sy) return base;
      const hoje = new Date();
      const anosAlvo = Math.floor(((ano * 12 + mes) - (sy * 12 + sm)) / 12);
      const anosHoje = Math.max(0, Math.floor(((hoje.getFullYear() * 12 + (hoje.getMonth() + 1)) - (sy * 12 + sm)) / 12));
      return +(base * Math.pow(1.08, anosAlvo - anosHoje)).toFixed(2);
    };
    const mesLabelCurto = (ano, mes) => `${String(mes).padStart(2, "0")}/${ano}`;

    // ----- Ciclo de faturamento (periodicidade + fechamento) -----
    const INTERVALO = { mensal: 1, trimestral: 3, semestral: 6, anual: 12 };
    const absM = (y, m) => y * 12 + m;
    const ymFromAbs = (a) => [Math.floor((a - 1) / 12), ((a - 1) % 12) + 1];
    const diasFechDe = (r) => (r && r.fechamentoDiasAntes !== null && Number.isFinite(r.fechamentoDiasAntes))
      ? r.fechamentoDiasAntes : (Number.isFinite(data.fechamentoDiasAntes) ? data.fechamentoDiasAntes : 5);
    const startDe = (r) => r?.dataInicioContrato || r?.dataInternacao || "";
    const cicloInfo = (startIso, per, y, m) => {
      const n = INTERVALO[per || "mensal"] || 1;
      if (n <= 1) return { eh: true, meses: [[y, m]], n: 1 };
      const [sy, sm] = (startIso || "").split("-").map(Number);
      if (!sy) return { eh: true, meses: [[y, m]], n: 1 };
      const idx = absM(y, m) - absM(sy, sm);
      if (idx < 0 || idx % n !== (n - 1)) return { eh: false, meses: [], n };
      const base = absM(y, m); const meses = [];
      for (let k = n - 1; k >= 0; k--) meses.push(ymFromAbs(base - k));
      return { eh: true, meses, n };
    };
    const mesDecimo = (startIso, per, y) => { for (let mm = 12; mm >= 1; mm--) if (cicloInfo(startIso, per, y, mm).eh) return mm; return null; };
    const fechamentoDateR = (r, y, m) => { const d = new Date(y, m - 1, Math.min(Number(r?.diaPagamento) || 10, 28)); d.setDate(d.getDate() - diasFechDe(r)); return d; };
    // Fatura já "fechada": mês atual/passado OU mês futuro cuja data de fechamento já chegou (antecipada).
    const hojeD = new Date(); hojeD.setHours(23, 59, 59, 999);
    const jaFechada = (r, y, m) => absM(y, m) <= absM(hojeD.getFullYear(), hojeD.getMonth() + 1) || fechamentoDateR(r, y, m) <= hojeD;
    // mês da fatura (cobrança) a que pertence um consumo
    const faturaMesConsumo = (isoData, r) => {
      const [yy, mm, dd] = (isoData || "").slice(0, 10).split("-").map(Number);
      if (!yy) return (isoData || "").slice(0, 7);
      const d = new Date(yy, mm - 1, dd);
      let y = yy, m = mm;
      let g0 = 0;
      while (d > fechamentoDateR(r, y, m) && g0 < 24) { m += 1; if (m > 12) { m = 1; y += 1; } g0++; }
      const per = r?.periodicidadePagamento || "mensal";
      const st = startDe(r);
      let g = 0;
      while (g < 24) { if (cicloInfo(st, per, y, m).eh) break; m += 1; if (m > 12) { m = 1; y += 1; } g++; }
      return `${y}-${String(m).padStart(2, "0")}`;
    };

    // Consumos → itens da fatura do ciclo de cobrança
    data.consumos.forEach((c) => {
      if (c.valorCobrado <= 0) return;
      const r = resById[c.residenteId];
      const mes = faturaMesConsumo(c.data, r);
      const f = ensure(c.residenteId, mes);
      f.itens.push(c);
      f.total += c.valorCobrado;
    });

    // Mensalidade RECORRENTE: gera para cada mês do mês de entrada até o mês atual.
    // 13º proporcional entra apenas em dezembro de cada ano (quando marcado no contrato).
    const hoje = new Date();
    const curY = hoje.getFullYear();
    const curM = hoje.getMonth() + 1;
    data.residentes.forEach((r) => {
      if ((r.status || "Ativo") !== "Ativo") return;
      const base = Number(r.valorMensalidade) || 0;
      if (base <= 0) return;
      const inicio = r.dataInternacao || r.dataInicioContrato || "";
      const reaj = r.dataInicioContrato || r.dataInternacao || "";
      const [iy, im] = inicio.split("-").map(Number);
      if (!iy) return;
      let y = iy;
      let m = im || 1;
      let guarda = 0;
      const per = r.periodicidadePagamento || "mensal";
      const st = startDe(r);
      while (absM(y, m) <= absM(curY, curM) + 12 && guarda < 600) {
        guarda += 1;
        const info = cicloInfo(st, per, y, m);
        const mesLoop = `${y}-${String(m).padStart(2, "0")}`;
        const futuroSemFatura = absM(y, m) > absM(curY, curM) && !map[`${r.id}_${mesLoop}`] && !jaFechada(r, y, m);
        if (info.eh && !futuroSemFatura) {
          const mes = mesLoop;
          const f = ensure(r.id, mes);
          if (absM(y, m) > absM(curY, curM)) f.antecipada = true;
          let somaMens = 0;
          info.meses.forEach(([yy, mm]) => {
            if (absM(yy, mm) >= absM(iy, im || 1)) somaMens += mensalidadeVigenteMes(reaj, base, yy, mm);
          });
          if (somaMens > 0) {
            const desc = info.n > 1
              ? `Mensalidade ${per} (${info.n} meses: ${mesLabelCurto(info.meses[0][0], info.meses[0][1])} a ${mesLabelCurto(y, m)})`
              : `Mensalidade ${mesLabelCurto(y, m)}`;
            f.extras.push({ descricao: desc, valor: +somaMens.toFixed(2) });
            f.total += +somaMens.toFixed(2);
            const pct = Number(r.descontoPercentual) || 0;
            if (pct > 0) {
              const desconto = +(+somaMens.toFixed(2) * pct / 100).toFixed(2);
              f.extras.push({ descricao: "Desconto na mensalidade", valor: -desconto });
              f.total -= desconto;
            }
          }
          if (r.cobrarDecimoTerceiro && mesDecimo(st, per, y) === m) {
            const integral = info.n > 1;
            let meses = integral ? 12 : (iy === y ? Math.max(0, 13 - (im || 1)) : 12);
            if (meses > 0) {
              const dez = mensalidadeVigenteMes(reaj, base, y, 12);
              const decimo = +((dez / 12) * meses).toFixed(2);
              f.extras.push({ descricao: integral ? "13º Mensalidade (integral)" : `13º Mensalidade proporcional (${meses}/12 meses)`, valor: decimo });
              f.total += decimo;
            }
          }
        }
        m += 1;
        if (m > 12) { m = 1; y += 1; }
      }
    });

    // Serviços do residente: recorrente = todo mês com reajuste anual pelo contrato; avulso = só no mês marcado.
    (data.residenteServicos || []).forEach((s) => {
      if (s.ativo === false) return;
      const r = resById[s.residenteId];
      if (!r || (r.status || "Ativo") !== "Ativo") return;
      const base = Number(s.valor) || 0;
      if (base <= 0) return;
      const reaj = r.dataInicioContrato || r.dataInternacao || "";
      const per = r.periodicidadePagamento || "mensal";
      const st = startDe(r);
      if (s.recorrente) {
        const [ay, am] = (s.dataAdicao || "").split("-").map(Number);
        if (!ay) return;
        let y = ay, m = am || 1, guarda = 0;
        while (absM(y, m) <= absM(curY, curM) + 12 && guarda < 600) {
          guarda += 1;
          const info = cicloInfo(st, per, y, m);
          const mesLoopS = `${y}-${String(m).padStart(2, "0")}`;
          const futuroSemFaturaS = absM(y, m) > absM(curY, curM) && !map[`${s.residenteId}_${mesLoopS}`] && !jaFechada(r, y, m);
          if (info.eh && !futuroSemFaturaS) {
            let somaS = 0;
            info.meses.forEach(([yy, mm]) => {
              if (absM(yy, mm) >= absM(ay, am || 1)) somaS += mensalidadeVigenteMes(reaj, base, yy, mm);
            });
            if (somaS > 0) {
              const f = ensure(s.residenteId, mesLoopS);
              const suf = info.n > 1 ? ` (x${info.n})` : "";
              f.extras.push({ descricao: `Serviço: ${s.nome}${suf}`, valor: +somaS.toFixed(2) });
              f.total += +somaS.toFixed(2);
            }
          }
          m += 1; if (m > 12) { m = 1; y += 1; }
        }
      } else if (s.cobrarMes) {
        // avulso: cai na fatura do ciclo que contém o mês configurado
        const [cy, cm] = s.cobrarMes.split("-").map(Number);
        let y = cy, m = cm, g = 0;
        while (g < 24) { if (cicloInfo(st, per, y, m).eh) break; m += 1; if (m > 12) { m = 1; y += 1; } g++; }
        const f = ensure(s.residenteId, `${y}-${String(m).padStart(2, "0")}`);
        f.extras.push({ descricao: `Serviço: ${s.nome}`, valor: base });
        f.total += base;
      }
    });

    // Ajustes manuais salvos no banco (itens manuais + observação). Cria faturas avulsas se necessário.
    Object.entries(data.faturasAjustes || {}).forEach(([key, aj]) => {
      const idx = key.lastIndexOf("_");
      const residenteId = key.slice(0, idx);
      const mes = key.slice(idx + 1);
      const f = ensureManual(residenteId, mes);
      if (!f.key) return;
      f.manual = aj.itens || [];
      f.observacao = aj.observacao || "";
      f.avulsa = f.itens.length === 0 && f.extras.length === 0;
      (aj.itens || []).forEach((it) => { f.total += Number(it.valor) || 0; });
    });

    return Object.values(map)
      .map((f) => {
        const [y, m] = f.mes.split("-").map(Number);
        const r = resById[f.residenteId];
        const diaBruto = Math.max(1, Math.min(Number(r?.diaPagamento) || 10, 31));
        const ultimoDia = new Date(y, m, 0).getDate();
        const dia = Math.min(diaBruto, ultimoDia);
        const venc = `${y}-${String(m).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
        const bol = boletos?.[f.key];
        const totalCalc = +f.total.toFixed(2);
        const totalBoleto = bol && Number(bol.valor) > 0 ? +Number(bol.valor).toFixed(2) : null;
        return { ...f, total: totalBoleto ?? totalCalc, totalCalculado: totalCalc, totalBoleto, vencimento: bol?.vencimento || venc, paga: !!data.faturasPagas[f.key], cancelada: !!(data.faturasCanceladas || {})[f.key], dataPagamento: (data.faturasPagamentos || {})[f.key] || "" };
      })
      .sort((a, b) => b.mes.localeCompare(a.mes) || (resById[a.residenteId]?.nome || "").localeCompare(resById[b.residenteId]?.nome || ""));
  }, [data.consumos, data.faturasPagas, data.faturasAjustes, data.faturasCanceladas, data.faturasPagamentos, data.residentes, data.residenteServicos, boletos]);

  // ---------- ALERTAS DE REPOSIÇÃO ----------
  const alertasReposicao = useMemo(() => {
    const LIMITE = 5;
    const alerts = [];
    const seen = new Set();
    Object.entries(data.estoqueResidente).forEach(([rid, prods]) => {
      Object.entries(prods || {}).forEach(([pid, qty]) => {
        if (qty > 0 && qty <= LIMITE) {
          alerts.push({ residenteId: rid, produtoId: pid, quantidade: qty, tipo: "baixo" });
          seen.add(rid + "_" + pid);
        }
      });
    });
    data.consumos.forEach((c) => {
      if (c.qtdEmpresa <= 0) return;
      const key = c.residenteId + "_" + c.produtoId;
      const atual = data.estoqueResidente[c.residenteId]?.[c.produtoId] || 0;
      if (atual === 0 && !seen.has(key)) {
        seen.add(key);
        alerts.push({ residenteId: c.residenteId, produtoId: c.produtoId, quantidade: 0, tipo: "esgotado" });
      }
    });
    return alerts;
  }, [data.estoqueResidente, data.consumos]);

  const value = {
    ...data,
    data,
    residentesAtivos: (data.residentes || []).filter((r) => (r.status || "Ativo") === "Ativo"),
    estoqueEmpresa,
    loading,
    auth, login, logout,
    getProduto, getResidente, estoqueDoResidente, estoqueDaEmpresa, janelaConsumo,
    registrarFornecimento, registrarConsumo, editarFornecimento, excluirFornecimento, editarConsumo, excluirConsumo, salvarProduto, entradaEstoqueEmpresa, excluirProduto, salvarPrecosMassa,
    salvarResidente, inativarResidente, reativarResidente, faturas, marcarFaturaPaga, alertasReposicao, refresh,
    salvarFaturaAjuste, excluirFaturaAjuste, excluirFatura, restaurarFatura, baixaMassaFaturas, excluirMassaFaturas, enviarFaturaWhatsapp, enviarFaturaWhatsappPdf, gerarBoletoFatura, cancelarBoletoFatura, boletos, historicoBoletos, obterPixFatura, enviarPixFatura, baixaPixFatura, baixarFaturaPdf, carregarBoletos, baixarContrato, enviarContratoEmail, baixarDocumento, enviarDocumentoEmail, baixarRelatorio,
    salvarFornecedor, excluirFornecedor, salvarCategoriaConta, updateCategoriaConta, excluirCategoriaConta, salvarOrcamentos,
    salvarConta, excluirConta, baixaMassaContas, excluirMassaContas, gerarRecorrentes, testarAvisoContas,
    salvarContaReceber, excluirContaReceber,
    salvarInventario, excluirInventario,
    empresa: data.empresa || empresaFallback, salvarEmpresa,
    salvarCategoria, excluirCategoria,
    uploadDocumento, getDocumento, getDocumentoBlob, getDocumentoThumb, deleteDocumento, patchDocumento, getDocumentoFamiliaBlob,
    getProntuario, addRegistroProntuario, deleteRegistroProntuario, updateRegistroProntuario, updateProntuarioInfo,
    gerarAnotacaoInteligente,
    getReceituarios, createReceituario, deleteReceituario,
    getProfissionais, createProfissional, updateProfissional, deleteProfissional,
    getReceitaModelos, createReceitaModelo, deleteReceitaModelo,
    getMedicacaoModelos, createMedicacaoModelo, deleteMedicacaoModelo,
    perfil, isAdmin, acessoFinanceiro, verValores, podeReceituario, podeApagarAnotacao, podeEditarAnotacaoSempre,
    getNotificacoes, limparNotificacoes, enviarNotificacao, runFechamento, getWhatsappStatus,
    getUsuarios, createUsuario, updateUsuario, deleteUsuario,
    trocarSenha, trocarFoto, podeVer, podeAcao, salvarServicoExtra, excluirServicoExtra, salvarResidenteServico, excluirResidenteServico,
    chatOpen: chatUI.open, chatTab: chatUI.tab, openChat, closeChat, naoLidasChat, carregarNaoLidasChat,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};
